import './applianceRetailers.scss';

import { graphql, useStaticQuery } from 'gatsby';

import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import GatsbyImageWrapper from '../GatsbyImageWrapper';
import { IAppliance } from '../../interfaces';
import { IRetailer } from '../../interfaces/interfaces';
import React from 'react';
import rightArrowIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Right-Arrow-Icon.svg';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export default function ApplianceRetailers(props: { appliance: IAppliance }) {
    const { appliance } = props;
    const { currentLocale } = usePageLocalization();
    const { t } = useTranslation('layout');
    const { retailers } = useStaticQuery(
        graphql`
            query {
                retailers: allProductsJson(
                    filter: {
                        category: { regex: "^/Englander/CMS/Retailers/" }
                    }
                ) {
                    nodes {
                        code
                        title {
                            en_CA
                            en_US
                            fr_CA
                        }
                        images {
                            en_CA
                            en_US
                            fr_CA
                        }
                        fields {
                            ...productsFieldsImagesFragment
                        }
                        attributes {
                            url {
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                            }
                            available_country {
                                values {
                                    en_CA
                                    id
                                }
                            }
                        }
                    }
                }
            }
        `
    );

    const onlineRetailerCodes =
        appliance?.online_retailers?.map(
            (retailer: { code: string }) => retailer.code
        ) || [];
    const storeRetailerCodes =
        appliance?.store_retailers?.map(
            (retailer: { code: string }) => retailer.code
        ) || [];
    const onlineRetailers = retailers?.nodes?.filter((node: any) =>
        onlineRetailerCodes.some(
            (retailerCode: string) => node.code === retailerCode
        )
    );
    const storeRetailers = retailers?.nodes?.filter((node: any) =>
        storeRetailerCodes.some(
            (retailerCode: string) => node.code === retailerCode
        )
    );
    const current_country =
        currentLocale.split('_')[1] === 'CA' ? 'Canada' : 'United States';
    const filteredStoreRetailers = storeRetailers.filter((retailer: any) => {
        const availableCountry =
            retailer?.attributes?.available_country?.values?.map(
                (e: any) => e?.en_CA
            );

        return !availableCountry || availableCountry.includes(current_country);
    });
    const filteredOnlineRetailers = onlineRetailers.filter((retailer: any) => {
        const availableCountry =
            retailer?.attributes?.available_country?.values?.map(
                (e: any) => e?.en_CA
            );

        return !availableCountry || availableCountry.includes(current_country);
    });

    const isSectionHide = () =>
        !(
            filteredStoreRetailers &&
            filteredStoreRetailers?.length > 0 &&
            filteredOnlineRetailers &&
            filteredOnlineRetailers?.length > 0
        );

    return (
        <div className='appliance-retailers-container' hidden={isSectionHide()}>
            <div className='appliance-banner'>
                {t('appliance.retailers-title')}
            </div>
            {filteredStoreRetailers?.length > 0 && (
                <RetailerSection
                    className='first'
                    retailerList={filteredStoreRetailers}
                    title={t('appliance.retailers-in-store')}
                />
            )}
            {filteredOnlineRetailers?.length > 0 && (
                <RetailerSection
                    retailerList={filteredOnlineRetailers}
                    title={t('appliance.retailer-online')}
                />
            )}
            <Button
                className='find-retailer'
                label={t('appliance.find-near-retailer')}
                iconRight={rightArrowIcon}
                link='/where-to-buy/'
            />
        </div>
    );
}

function RetailerSection(props: {
    retailerList: any[];
    title: string;
    className?: string;
}) {
    const { currentLocale } = usePageLocalization();
    return (
        <div className={`retailer-section ${props?.className}`}>
            <div className='retailer-section-title'>{props.title}</div>

            {props.retailerList.map((retailer: IRetailer) => {
                return (
                    <div className='single-retailer' key={retailer.code}>
                        {retailer?.fields?.images?.[0] ? (
                            <a
                                href={
                                    retailer?.attributes?.url?.values[0][
                                        currentLocale
                                    ]
                                }
                                target='_blank'
                                rel='noreferrer'
                            >
                                <GatsbyImageWrapper
                                    fields={
                                        retailer.fields
                                    }                                    
                                />
                            </a>
                        ) : (
                            <a
                                href={
                                    retailer?.attributes?.url?.values[0][
                                        currentLocale
                                    ]
                                }
                                target='_blank'
                                rel='noreferrer'
                                className='no-image-retailer'
                            >
                                {retailer?.title[currentLocale]}
                            </a>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
